import HBMessage from "@/components/HBMessage";
import PinSuccessMessage from "@/components/PinSuccessMessage";
import { EVENT } from "@/constants/event";
import useAppStore from "@/context/store";
import { deletePin, flashPin } from "@/services";
import { button_click } from "@/tracker/collecter/buttonClick";
import { useMemoizedFn } from "ahooks";
import { uniqBy } from "lodash";
import { useMemo, useRef } from "react";
import { create } from "zustand";
import { checkLogin, openBindTelModal } from "../open";

const useStore = create(() => {
  return {
    createdPins: [],
  };
});

const pinCreateStore = useStore;

function updateCreatedPins(pin: IPin, isRemove?: boolean) {
  if (!pin) {
    return;
  }

  pinCreateStore.setState(({ createdPins }) => {
    return {
      createdPins: isRemove
        ? createdPins.filter((p) => p.pin_id !== pin.pin_id)
        : uniqBy(createdPins.concat(pin), "pin_id"),
    };
  });
}

/**
 * 采集创建
 * - 提供采集、快采操作
 * - 采集成功后，将采集数据存储到本地
 */
export function usePinCreate({
  pin,
  onPinSuccess,
  onPinWarn,
  hasPinCreated,
  lastBoards,
  boardMatchingResult,
  enableBoardRecommend,
}: {
  pin: IPin;
  onPinSuccess?: (pin: IPin) => void;
  onPinWarn?: (err: any) => void;
  hasPinCreated?: boolean;
  lastBoards?: IBoardInfo[];
  boardMatchingResult?: any;
  enableBoardRecommend?: boolean; // 是否启用画板推荐功能（AB实验控制）
}) {
  const pinitModalRef = useRef(null);
  const { createdPins } = useStore((state) => state);

  const isCreated = useMemo(() => {
    return createdPins.some((p) => p.via === pin?.pin_id) || hasPinCreated;
  }, [createdPins, pin, hasPinCreated]);

  const {
    lastBoards: globalLastBoards,
    lastBoardsReady,
    deltaPinCount,
    basePinCount,
  } = useAppStore(
    ({ lastBoards, lastBoardsReady, deltaPinCount, basePinCount }) => ({
      lastBoards,
      lastBoardsReady,
      deltaPinCount,
      basePinCount,
    })
  );

  lastBoards = lastBoards || globalLastBoards;

  /**
   * 判断是否是首次采集且采集到"未整理"（需要隐藏成功消息）
   * 注意：只有在推荐版本（enableBoardRecommend 为 true）时才隐藏提示
   * 无推荐版本（enableBoardRecommend 为 false）时，应该显示提示
   */
  const shouldHideSuccessMessage = useMemoizedFn((pin: IPin) => {
    // 如果未启用画板推荐功能（无推荐版本），不隐藏提示，保持原有逻辑
    if (!enableBoardRecommend) {
      return false;
    }

    const isUnclassifiedBoard =
      pin?.board?.board_id === 0 || pin?.board?.title === "未整理的采集";
    if (!isUnclassifiedBoard) {
      return false;
    }

    // 判断是否是新用户（无画板）
    // 注意：lastBoards 可能包含"未整理"画板，所以需要过滤掉它
    const userCreatedBoards = (lastBoards || []).filter(
      (board) => board.board_id !== 0 && board.title !== "未整理的采集"
    );
    const isNewUser =
      lastBoardsReady &&
      (!lastBoards ||
        lastBoards.length === 0 ||
        userCreatedBoards.length === 0);
    if (!isNewUser) {
      return false;
    }

    // 判断是否是第一次采集（采集前 pinCount 为 0，采集后为 1）
    // 注意：在 onFlash 中，采集成功后 deltaPinCount 还未更新（因为 incrementPinDelta 是在 onPinSuccess 回调中调用的）
    // 所以此时 currentPinCount 应该是采集前的值（0）
    // 在 sucessCallback 中，采集已经完成，deltaPinCount 可能已经更新，所以 currentPinCount 可能是 1
    // 为了准确判断，我们从 store 实时获取最新的状态
    const store = useAppStore.getState();
    const currentDeltaPinCount = store.deltaPinCount || 0;
    const currentBasePinCount = store.basePinCount || 0;
    const currentPinCount = currentBasePinCount + currentDeltaPinCount;
    // 如果是首次采集，采集前应该是 0，采集后应该是 1
    // 但由于状态更新的时机问题，我们判断：如果当前是 0 或 1，且是未整理画板，则认为是首次采集
    // 注意：在 onFlash 中，采集成功后 deltaPinCount 还未更新，所以 currentPinCount 应该是采集前的值
    // 如果是首次采集，采集前 currentPinCount 应该是 0
    return currentPinCount <= 1;
  });

  /**
   * 快采
   */
  const onFlash = useMemoizedFn(async (check) => {
    const user = await checkLogin({
      source_location: "快采",
    });

    // 处理关闭登录弹窗场景
    if (!user) {
      return;
    }

    if (!user?.tel) {
      await openBindTelModal();
    }

    if (!pin) {
      return;
    }

    // 优先使用匹配结果中的推荐画板，否则使用最近使用的画板
    const lastBoard = lastBoards?.[0] || null;
    // 当画板数 <= 5 且有推荐画板时，使用推荐画板
    const isRecommendedBoard =
      lastBoards?.length <= 5 &&
      !!boardMatchingResult?.recommendedBoard?.board_id;
    const boardId = isRecommendedBoard
      ? boardMatchingResult.recommendedBoard.board_id
      : lastBoards?.length > 0
      ? lastBoards[0]?.board_id || null
      : null;

    const result = await flashPin({
      via: pin.pin_id,
      text: pin.raw_text,
      board_id: boardId,
      ...(check ? { check } : null),
    });

    if (!result.err) {
      if (result.warning) {
        onPinWarn?.(result);
      } else {
        const _pin = result.pin;
        // 如果是首次采集且采集到"未整理"，则不显示成功消息
        const shouldHide = shouldHideSuccessMessage(_pin);
        if (!shouldHide) {
          PinSuccessMessage.open({
            pin: _pin,
            onUndo: (pin) => {
              updateCreatedPins(pin, true);
            },
          });
        }

        // 设置已完成采集的数据
        updateCreatedPins(result.pin);

        onPinSuccess?.(result.pin);

        const boardSource = boardId
          ? lastBoard?.extra?.collab
            ? "画板_多人"
            : isRecommendedBoard
            ? "画板_推荐创建"
            : "画板"
          : "未整理";

        button_click(null, {
          other: {
            button_name: "采集完成",
            pin_id: pin?.pin_id,
            board_id: boardId,
            button_source: `快采至${boardSource}`,
            file_id: pin?.file_id,
          },
        });
      }
    }
  });

  const sucessCallback = useMemoizedFn((_pin) => {
    // 如果是首次采集且采集到"未整理"，则不显示成功消息
    const shouldHide = shouldHideSuccessMessage(_pin);
    if (!shouldHide) {
      PinSuccessMessage.open({
        pin: _pin,
        onUndo: (pin) => {
          updateCreatedPins(pin, true);
        },
      });
    }

    updateCreatedPins(_pin);
    onPinSuccess?.(_pin);

    // 判断是否采集到了推荐的画板（用于埋点）
    // 1. 用户自己的推荐画板（从用户现有画板中匹配）
    const isRecommendedUserBoard =
      lastBoards?.length <= 5 &&
      boardMatchingResult?.recommendedBoards?.some(
        (board) => board.board_id === _pin.board_id
      );

    // 2. 推荐创建的画板（通过标题匹配，同在画板中推荐的新画板）
    // 因为 recommendedRelatedBoards 中的画板还未创建，没有真实 board_id
    // 所以通过标题匹配来判断
    const recommendedRelatedTitles =
      boardMatchingResult?.recommendedRelatedBoards?.map((b) => b.title) || [];
    const isRecommendedRelatedBoard =
      lastBoards?.length <= 5 &&
      recommendedRelatedTitles.length > 0 &&
      recommendedRelatedTitles.includes(_pin.board?.title);

    const boardSource =
      _pin.board.title === "未整理的采集"
        ? "未整理"
        : _pin.board?.extra?.collab
        ? "画板_多人"
        : isRecommendedRelatedBoard
        ? "画板_推荐创建"
        : "画板";

    button_click(null, {
      other: {
        button_name: "采集完成",
        pin_id: pin.pin_id,
        board_id: _pin.board_id,
        file_id: _pin.file_id,
        button_source: `采集至${boardSource}`,
      },
    });

    useAppStore.getState().refreshLastBoards();
  });

  /**采集 */
  const onCreate = useMemoizedFn(async () => {
    const user = await checkLogin({
      source_location: "采集",
    });

    // 处理关闭登录弹窗场景
    if (!user) {
      return;
    }

    if (!user?.tel) {
      // 实时获取用户信息，避免未更新信息而导致弹窗
      const user = await useAppStore.getState().refreshUser();
      if (user?.tel) return user;

      await openBindTelModal();
    }

    const { default: PinCreatorModal } = await import(
      "@/components/PinCreatorModal"
    );

    pinitModalRef.current = PinCreatorModal.open({
      pin,
      onOk: sucessCallback,
      afterClose: () => {
        pinitModalRef.current = null;
      },
    });
  });

  /**
   * 撤销采集
   */
  const onUndo = useMemoizedFn(async (pin: IPin) => {
    // 找到要删除的pin
    const delPin = hasPinCreated
      ? pin
      : createdPins.find((p) => p.via === pin.pin_id);

    if (delPin) {
      const res = await deletePin({ pin_id: delPin.pin_id });
      if (!res.err) {
        HBMessage.success("撤销成功");
      }
    }

    // 未整理采集瀑布流
    if (hasPinCreated) {
      window.dispatchEvent(
        new CustomEvent(EVENT.DELETE_PIN, { detail: delPin?.pin_id })
      );
    }

    updateCreatedPins(delPin, true);
  });

  return {
    isCreated,
    onFlash,
    onCreate,
    pinitModalRef,
    sucessCallback,
    onUndo,
  };
}
